// Player.jsx
import { useRapier, RigidBody } from '@react-three/rapier';
import { useFrame } from '@react-three/fiber';
import { useKeyboardControls } from '@react-three/drei';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import useGame from './stores/useGame';

export default function Player() {
  const body = useRef();
  // index.js KeyboardControls와 연결되어 있음.
  const [subscribeKeys, getKeys] = useKeyboardControls();
  const { rapier, world } = useRapier();

  const [smoothedCameraPosition] = useState(
    // camera position 초기값 설정으로 player까지 가는 애니메이션 설정
    () => new THREE.Vector3(10, 10, 10),
  );
  const [smoothedCameraTarget] = useState(() => new THREE.Vector3());
  const start = useGame((state) => state.start);
  const end = useGame((state) => state.end);
  const restart = useGame((state) => state.restart);
  const blocksCount = useGame((state) => state.blocksCount);

  /**
   * Jump
   */
  const jump = useCallback(() => {
    if (!body.current) return;

    // 1) 원점: 구의 아래쪽 가장자리보다 살짝 더 아래
    // RigidBody의 월드 위치를 {x:number, y:number, z:number}로 반환.
    const origin = body.current.translation();
    // 구(반지름 0.3)의 아래쪽 가장자리보다 살짝 더 아래에서 레이를 쏘려고 0.31만큼 내림
    origin.y -= 0.31; // radius 0.3 + epsilon

    // 2) 아래 방향(-y로 ,단위 벡터)
    const direction = { x: 0, y: -1, z: 0 };

    // 3) Ray 생성 + 캐스팅
    const ray = new rapier.Ray(origin, direction); // 구의 -0.31지점에서, 아래 방향으로
    // ray: 위에서 만든 레이
    // maxToi: 최대 충돌 길이(월드 유닛) — 여기선 10
    // solid: true면 콜라이더 내부에서 시작해도 즉시 교차로 간주(= “고체처럼” 취급)
    const hit = world.castRay(ray, 10, true); // maxToi=10, solid=true

    // 4) 버전별 필드 호환, hit.toi(이전 버전) == hit.TimeOfImpact(히트 지점까지의 거리 = t * 거리)
    const toi = hit ? hit.timeOfImpact ?? hit.toi : null;

    // 5) 접지 임계 판정 후 점프
    if (toi !== null && toi < 0.15) {
      body.current.applyImpulse({ x: 0, y: 0.5, z: 0 }, true);
    }
  }, [world, rapier]);

  const reset = () => {
    body.current.setTranslation({ x: 0, y: 1, z: 0 });
    body.current.setLinvel({ x: 0, y: 0, z: 0 });
    body.current.setAngvel({ x: 0, y: 0, z: 0 });
  };

  useEffect(() => {
    const unsubscribeReset = useGame.subscribe(
      (state) => {
        return state.phase;
      },
      (value) => {
        if (value === 'ready') reset();
      },
    );

    const unsubscribeJump = subscribeKeys(
      // state: const state = getKeys();와 같은 값을 리턴
      (state) => state.jump, // selector
      // jump boolean 값을 return
      (pressed) => {
        if (pressed) jump();
      }, // listener
    );

    // phase
    const unsubscribeAny = subscribeKeys(() => {
      start();
    });

    // cleanup: 마운트 해제/리렌더 시 안전
    return () => {
      unsubscribeReset();
      unsubscribeJump();
      unsubscribeAny();
    };
  }, [subscribeKeys, jump]);

  useFrame((state, delta) => {
    /**
     * Controls
     */
    const { forward, backward, leftward, rightward } = getKeys();

    const impulse = { x: 0, y: 0, z: 0 };
    const torque = { x: 0, y: 0, z: 0 };

    const impulseStrength = 0.6 * delta;
    const torqueStrength = 0.2 * delta;

    if (forward) {
      impulse.z -= impulseStrength; // 직접 밀면서 -Z로 이동
      torque.x -= torqueStrength; // X축으로 회전 토크 = -Z로 굴러가게끔(부호 맞춘 것)
    }
    if (backward) {
      impulse.z += impulseStrength;
      torque.x += torqueStrength;
    }
    if (rightward) {
      impulse.x += impulseStrength;
      torque.z -= torqueStrength;
    }
    if (leftward) {
      impulse.x -= impulseStrength;
      torque.z += torqueStrength;
    }

    body.current.applyImpulse(impulse);
    // 회전 TorqueImpulse 이해하기
    // 바퀴(또는 구체)의 전진 방향은 항상 회전축과 직교한다.
    // R3F(위=Y) 기준으로 X축으로 회전시키면 Z축 방향(보통 −Z)으로 굴러 전진한다.
    // 바닥과의 마찰이 있어야 회전이 병진으로 바뀌며, 마찰이 없으면 제자리에서 헛돈다.
    // Blender에서 X축 회전(R → X) 을 떠올리면 이해가 쉽다(좌표계 차이는 감안).
    // 자동차의 경우 두 바퀴를 잇는 차축(X축) 이 회전축이고, 차량은 그 축에 직교하는 진행축(앞/뒤)으로 나아간다.
    body.current.applyTorqueImpulse(torque);

    /**
     * Camera
     */
    const bodyPosition = body.current.translation();

    // new THREE.Vector3()에 값을 복사해 두는 게 안전
    // const [cameraPosition] = useState(() => new THREE.Vector3());로 만들어서 재사용하는 방법도 있음.
    const cameraPosition = new THREE.Vector3();
    cameraPosition.copy(bodyPosition); // copy 대신 set도 가능
    cameraPosition.z += 2.25;
    cameraPosition.y += 0.65;

    const cameraTarget = new THREE.Vector3();
    cameraTarget.copy(bodyPosition);
    cameraTarget.y += 0.25;

    // Vector3.lerp(v: THREE.Vector3Like, alpha: number)
    // Linear Interpolation(lerp): 이전 값을 담아서 두 vector3 사이에 중간점을 찾음.
    smoothedCameraPosition.lerp(cameraPosition, 5 * delta); // 비교 vec3 , alpha
    smoothedCameraTarget.lerp(cameraTarget, 5 * delta);

    // 카메라 포지션 지정과 카메라 방향 지정
    state.camera.position.copy(smoothedCameraPosition); // 카메라 world 위치 변경
    state.camera.lookAt(smoothedCameraTarget); // 회전 변경

    /**
     * Phases
     */
    if (bodyPosition.z < -(blocksCount * 4 + 2)) {
      end();
    }
    if (bodyPosition.y < -4) {
      restart();
    }
  });

  return (
    <RigidBody
      ref={body}
      canSleep={false} // 잠들면 키보드가 동작하지 않으므로 sleep false 설정
      colliders="ball"
      restitution={0.2}
      friction={1}
      position={[0, 1, 0]}
      linearDamping={0.5} // 선속 감쇠 (키에서 손을 떼면 서서히 멈추도록 0.5로 세팅)
      angularDamping={0.5} // 각속도 감쇠
    >
      <mesh castShadow>
        <icosahedronGeometry args={[0.3, 1]} />
        <meshStandardMaterial
          flatShading // segment 보이도록
          color="mediumpurple"
        />
      </mesh>
    </RigidBody>
  );
}
